import qz from 'qz-tray';

/**
 * Sets up printer configuration 
 * @param {String} selectedPrinter - The name of the selected printer
 * @param {Object} dimensions - Width and height dimensions
 * @returns {Object} - QZ Tray config object
 */
export const setupPrinterConfig = async (selectedPrinter, dimensions) => {
  const { pageWidth, pageHeight } = dimensions;
  
  // Create base printer config
  const config = qz.configs.create(selectedPrinter);
  
  // Add special configurations for Arabic text
  config.encode = 'UTF-8';
  config.density = 230; // Higher density for better text clarity
  config.altPrinting = true; // Alternative printing method that might handle Arabic better
  
  // Set rasterize option for better text rendering
  config.rasterize = true; // Important for complex text rendering
  config.interpolation = 'bicubic'; // Better image quality
  
  // Use pixel printing mode which handles non-standard characters better
  config.pixelMode = true;
  
  // Set essential print options
  await config.reconfigure({
    size: { width: pageWidth, height: pageHeight },
    margins: { top: 0, right: 0, bottom: 0, left: 0 },
    colorType: 'grayscale',
    orientation: 'portrait',
    units: 'mm',
    density: '230dpi', // Increased for clearer text
    scaleContent: false,
    bounds: {
      width: pageWidth,
      height: pageHeight
    }
  });
  
  return config;
};

/**
 * Opens cash drawer using QZ Tray
 * @param {String} selectedPrinter - The name of the selected printer
 */
export const openCashDrawer = async (selectedPrinter) => {
  // Create a separate config for raw commands
  const drawerConfig = qz.configs.create(selectedPrinter, {
    encoding: 'UTF-8',
    forceRaw: true
  });
  
  // Send drawer commands
  await qz.print(drawerConfig, [
    { type: 'raw', format: 'hex', data: '1B7000' },  // First pin
    { type: 'raw', format: 'hex', data: '1B7001' }   // Second pin
  ]);
};

/**
 * Prints receipt using QZ Tray
 * @param {String} selectedPrinter - The name of the selected printer
 * @param {Array} printData - Print data configuration
 * @param {Object} dimensions - Width and height dimensions
 * @returns {Promise} - QZ print promise
 */
export const printReceipt = async (selectedPrinter, printData, dimensions) => {
  try {
    // Check if QZ-Tray is connected
    if (!await qz.websocket.isActive()) {
      console.error('QZ Tray connection is not active. Cannot print.');
      throw new Error('Printer not connected. Please ensure QZ Tray is running.');
    }

    const config = await setupPrinterConfig(selectedPrinter, dimensions);
    const result = await qz.print(config, printData);
    return result;
  } catch (error) {
    console.error('Error in printReceipt:', error);
    // Try to provide more specific error messages
    if (error.message?.includes('Connection refused')) {
      throw new Error('Could not connect to QZ Tray. Please ensure it is running.');
    } else if (error.message?.includes('Printer not found')) {
      throw new Error(`Printer "${selectedPrinter}" not found. Please check printer name and connection.`);
    }
    throw error; // Re-throw the original error if we don't have a specific message
  }
};
